import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { toast } from "sonner";
import {
    CreateVideoUploadDocument,
    CompleteVideoUploadDocument,
    DeleteVideoAssetDocument,
} from "@/graphql/media.generated";
import { runVideoUpload } from "@/lib/videoUpload";

export interface VideoUploadResult {
    assetId: string;
}

export function useVideoUpload() {
    const [createUpload] = useMutation(CreateVideoUploadDocument);
    const [completeUpload] = useMutation(CompleteVideoUploadDocument);
    const [deleteUpload] = useMutation(DeleteVideoAssetDocument);
    const [
        uploading,
        setUploading,
    ] = useState<boolean>(false);
    const [
        progress,
        setProgress,
    ] = useState<number>(0);
    const [
        error,
        setError,
    ] = useState<string | null>(null);
    /** Live only while an upload is in flight; `cancel` aborts through it. */
    const abortRef = useRef<AbortController | null>(null);

    /**
     * Abandons the upload in flight.
     *
     * Unmounting the dialog does not stop a `fetch`, so closing it used to leave the remaining
     * parts uploading and `completeVideoUpload` firing afterwards — the upload finished with no UI
     * left to say so. The abort lands in the same catch as any other failure, which is what deletes
     * the asset that was created before the first byte moved.
     */
    const cancel = () => abortRef.current?.abort();

    /**
     * `title` is what the asset will be called everywhere it's listed. It used to be
     * collected by the upload dialog and then dropped, so the Library showed people
     * raw filenames.
     */
    const upload = async (
        file: File,
        organizationId: string | undefined,
        title?: string,
    ): Promise<VideoUploadResult> => {
        setUploading(true);
        setProgress(0);
        setError(null);
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            // `runVideoUpload` deletes the half-made asset on any failure, abort included.
            const assetId = await runVideoUpload(
                file,
                {
                    create: async () => {
                        const { data } = await createUpload({
                            variables: {
                                input: {
                                    organizationId,
                                    title: title?.trim() || file.name,
                                    filename: file.name,
                                    contentType: file.type,
                                    fileSizeBytes: file.size,
                                },
                            },
                        });
                        if (!data?.createVideoUpload) throw new Error("Upload could not be started");
                        return data.createVideoUpload;
                    },
                    complete: (input) => completeUpload({ variables: { input } }),
                    discard: (id) => deleteUpload({ variables: { id } }),
                },
                { onProgress: setProgress, signal: controller.signal },
            );
            return { assetId };
        } catch (e) {
            const message = (e as Error).message;
            // A cancel is not a failure, and reporting the abort's own message ("signal is aborted
            // without reason") as one would be nonsense. Same cleanup either way.
            const canceled = controller.signal.aborted;
            setError(canceled ? null : message);
            /**
             * Say it out loud. The inline message under the drop zone was the only signal, and it
             * sits below the fold once the form is scrolled — so a failed upload read as nothing
             * happening at all.
             */
            if (canceled) toast.info(`Upload of ${file.name} canceled.`);
            else toast.error(`${file.name} could not be uploaded. ${message}`);
            throw e;
        } finally {
            setUploading(false);
            abortRef.current = null;
        }
    };

    return { upload, cancel, uploading, progress, error };
}
